import type { CSSProperties } from 'react';
import {
  V2Badge,
  V2Card,
  V2CardHeader,
  Icon,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { JOURNAL_STATUS_LABEL } from '../../lib/types/finance.js';

export interface JournalLifecycleCounts {
  readonly draft: number;
  readonly submitted: number;
  readonly approved: number;
  readonly posted: number;
}

interface Stage {
  readonly id: 'draft' | 'submitted' | 'approved' | 'posted' | 'reversed';
  readonly label: string;
  readonly tone: V2BadgeTone;
  readonly ring: string;
}

const STAGES: readonly Stage[] = [
  { id: 'draft', label: JOURNAL_STATUS_LABEL.draft, tone: 'neutral', ring: 'var(--sk-fg-subtle)' },
  { id: 'submitted', label: JOURNAL_STATUS_LABEL.submitted, tone: 'warn', ring: 'var(--sk-warn)' },
  { id: 'approved', label: JOURNAL_STATUS_LABEL.approved, tone: 'info', ring: 'var(--sk-info)' },
  { id: 'posted', label: JOURNAL_STATUS_LABEL.posted, tone: 'success', ring: 'var(--sk-success)' },
  { id: 'reversed', label: 'برگشت‌خورده', tone: 'neutral', ring: 'var(--sk-border-strong)' },
];

const NODE: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  display: 'grid',
  placeContent: 'center',
  background: 'var(--sk-bg-elev)',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/finance`,
 *   overview tab, «چرخهٔ وضعیت سند» card
 * canonical pattern: tinted V2CardHeader + 5-node stepper
 *   (draft → submitted → approved → posted, dashed hop to reversed)
 * status: PARTIAL_REAL — the stage labels are the backend's own journal
 *   statuses (`JOURNAL_STATUS_LABEL`, mirrors @sakhtar/module-accounting
 *   labels.ts); the per-stage count under each badge comes from the real
 *   `GET .../accounting/journal-entries` list and is omitted while it is not
 *   ready. «برگشت‌خورده» carries no count: the list endpoint maps every row
 *   with `reversalJournalId: null` (apps/api/src/accounting/journal.controller.ts
 *   `mapJournalToDto(journal)` without the second argument), so reversed
 *   documents cannot be counted from it. The caption restates the posted-document
 *   immutability rule (AGENTS.md §2.4) and holds no data.
 */
export function OverviewLifecycleCard({
  counts,
}: {
  readonly counts: JournalLifecycleCounts | null;
}) {
  return (
    <V2Card>
      <V2CardHeader title="چرخهٔ وضعیت سند" icon="trend" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 14px 12px' }}>
        <div style={{ overflowX: 'auto' }}>
          <div
            role="list"
            aria-label="مراحل وضعیت سند حسابداری"
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              minWidth: 500,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 10,
                insetInlineStart: '10%',
                width: '60%',
                height: 2,
                borderRadius: 2,
                background: 'color-mix(in oklch, var(--sk-fg) 14%, transparent)',
              }}
            />
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 10,
                insetInlineStart: '70%',
                width: '20%',
                height: 0,
                borderTop: '2px dashed color-mix(in oklch, var(--sk-fg) 22%, transparent)',
              }}
            />
            {STAGES.map((stage) => {
              const count = counts && stage.id !== 'reversed' ? counts[stage.id] : null;
              return (
                <div
                  key={stage.id}
                  role="listitem"
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 7,
                  }}
                >
                  {stage.id === 'posted' ? (
                    <span
                      style={{
                        ...NODE,
                        color: '#fff',
                        background:
                          'color-mix(in oklch, var(--sk-mod-c, var(--sk-success)) var(--sk-mod-k, 70%), #0a0c10)',
                      }}
                    >
                      <Icon name="check" size="sm" />
                    </span>
                  ) : stage.id === 'reversed' ? (
                    <span style={{ ...NODE, boxShadow: `inset 0 0 0 2px ${stage.ring}` }}>
                      <Icon name="chevronRight" size="sm" />
                    </span>
                  ) : (
                    <span style={{ ...NODE, boxShadow: `inset 0 0 0 2px ${stage.ring}` }}>
                      <span
                        style={{ width: 8, height: 8, borderRadius: '50%', background: stage.ring }}
                      />
                    </span>
                  )}
                  <V2Badge tone={stage.tone}>{stage.label}</V2Badge>
                  {count !== null ? (
                    <span
                      className="sk-num"
                      style={{ fontSize: 12.5, color: 'var(--sk-fg-muted)' }}
                    >
                      {count.toLocaleString('fa-IR')}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.8,
            color: 'var(--sk-fg-muted)',
            paddingTop: 8,
            borderTop: '1px dashed var(--sk-divider)',
          }}
        >
          سند «ثبت‌شده» تغییرناپذیر است؛ اصلاح فقط با سند برگشتی انجام می‌شود.
        </div>
      </div>
    </V2Card>
  );
}
